import { useReducer } from "react";

import { IStoreDepositFilters } from "src/@types/store-deposit-maintenance";

import { DEFAULT_PER_PAGE, FIRST_PAGE } from "../types";

export interface DepositFilterDraft {
  readonly storeCode: number | undefined;
  readonly startDate: string | undefined;
  readonly endDate: string | undefined;
  readonly minAmount: number | undefined;
  readonly maxAmount: number | undefined;
  readonly nsu: string;
  readonly linkFilter: string | undefined;
}

export interface DepositFilterState {
  readonly draft: DepositFilterDraft;
  readonly applied: IStoreDepositFilters | undefined;
  readonly page: number;
  readonly perPage: number;
}

export type DepositFilterAction =
  | { type: "SET_STORE"; payload: number | undefined }
  | {
      type: "SET_PERIOD";
      payload: {
        startDate: string | undefined;
        endDate: string | undefined;
      };
    }
  | { type: "SET_MIN_AMOUNT"; payload: number | undefined }
  | { type: "SET_MAX_AMOUNT"; payload: number | undefined }
  | { type: "SET_NSU"; payload: string }
  | { type: "SET_LINK_FILTER"; payload: string | undefined }
  | { type: "APPLY_FILTERS"; payload: IStoreDepositFilters }
  | { type: "SET_PAGE"; payload: number }
  | { type: "SET_PAGE_SIZE"; payload: number };

const INITIAL_DRAFT: DepositFilterDraft = {
  storeCode: undefined,
  startDate: undefined,
  endDate: undefined,
  minAmount: undefined,
  maxAmount: undefined,
  nsu: "",
  linkFilter: undefined,
};

export const INITIAL_DEPOSIT_FILTER_STATE: DepositFilterState = {
  draft: INITIAL_DRAFT,
  applied: undefined,
  page: FIRST_PAGE,
  perPage: DEFAULT_PER_PAGE,
};

export function depositFilterReducer(
  state: DepositFilterState,
  action: DepositFilterAction,
): DepositFilterState {
  if (action.type === "SET_STORE") {
    return { ...state, draft: { ...state.draft, storeCode: action.payload } };
  }

  if (action.type === "SET_PERIOD") {
    return {
      ...state,
      draft: {
        ...state.draft,
        startDate: action.payload.startDate,
        endDate: action.payload.endDate,
      },
    };
  }

  if (action.type === "SET_MIN_AMOUNT") {
    return { ...state, draft: { ...state.draft, minAmount: action.payload } };
  }

  if (action.type === "SET_MAX_AMOUNT") {
    return { ...state, draft: { ...state.draft, maxAmount: action.payload } };
  }

  if (action.type === "SET_NSU") {
    return { ...state, draft: { ...state.draft, nsu: action.payload } };
  }

  if (action.type === "SET_LINK_FILTER") {
    return { ...state, draft: { ...state.draft, linkFilter: action.payload } };
  }

  if (action.type === "APPLY_FILTERS") {
    return { ...state, applied: action.payload, page: FIRST_PAGE };
  }

  if (action.type === "SET_PAGE") {
    return { ...state, page: action.payload };
  }

  if (action.type === "SET_PAGE_SIZE") {
    return { ...state, perPage: action.payload, page: FIRST_PAGE };
  }

  return state;
}

export interface DepositFilterMethods {
  readonly setStore: (storeCode: number | undefined) => void;
  readonly setPeriod: (
    startDate: string | undefined,
    endDate: string | undefined,
  ) => void;
  readonly setMinAmount: (value: number | undefined) => void;
  readonly setMaxAmount: (value: number | undefined) => void;
  readonly setNsu: (value: string) => void;
  readonly setLinkFilter: (value: string | undefined) => void;
  readonly applyFilters: (filters: IStoreDepositFilters) => void;
  readonly setPage: (page: number) => void;
  readonly setPageSize: (perPage: number) => void;
}

export interface UseDepositFilterReducer {
  readonly state: DepositFilterState;
  readonly methods: DepositFilterMethods;
}

export function useDepositFilterReducer(): UseDepositFilterReducer {
  const [state, dispatch] = useReducer(
    depositFilterReducer,
    INITIAL_DEPOSIT_FILTER_STATE,
  );

  const methods: DepositFilterMethods = {
    setStore: (storeCode: number | undefined): void =>
      dispatch({ type: "SET_STORE", payload: storeCode }),

    setPeriod: (
      startDate: string | undefined,
      endDate: string | undefined,
    ): void => dispatch({ type: "SET_PERIOD", payload: { startDate, endDate } }),

    setMinAmount: (value: number | undefined): void =>
      dispatch({ type: "SET_MIN_AMOUNT", payload: value }),

    setMaxAmount: (value: number | undefined): void =>
      dispatch({ type: "SET_MAX_AMOUNT", payload: value }),

    setNsu: (value: string): void => dispatch({ type: "SET_NSU", payload: value }),

    setLinkFilter: (value: string | undefined): void =>
      dispatch({ type: "SET_LINK_FILTER", payload: value }),

    applyFilters: (filters: IStoreDepositFilters): void =>
      dispatch({ type: "APPLY_FILTERS", payload: filters }),

    setPage: (page: number): void =>
      dispatch({ type: "SET_PAGE", payload: page }),

    setPageSize: (perPage: number): void =>
      dispatch({ type: "SET_PAGE_SIZE", payload: perPage }),
  };

  return { state, methods };
}
